import { lazy, Suspense, useEffect, useRef } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Box, CircularProgress } from '@mui/material';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuthStore } from '@/stores/authStore';
import { useUiStore } from '@/stores/uiStore';
import LoginPage from '@/pages/LoginPage';
import SsoCallbackPage from '@/pages/SsoCallbackPage';

// Route-level code splitting keeps the first paint small.
const HomePage = lazy(() => import('@/pages/HomePage'));
const MyWorkPage = lazy(() => import('@/pages/MyWorkPage'));
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('@/pages/ProjectDetailPage'));
const TimesheetPage = lazy(() => import('@/pages/TimesheetPage'));
const CommandCenterPage = lazy(() => import('@/pages/CommandCenterPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const ResourcesPage = lazy(() => import('@/pages/ResourcesPage'));
const ClientsPage = lazy(() => import('@/pages/ClientsPage'));
const PeoplePage = lazy(() => import('@/pages/PeoplePage'));
const PersonReviewPage = lazy(() => import('@/pages/PersonReviewPage'));
const WorkspacesPage = lazy(() => import('@/pages/WorkspacesPage'));
const ApprovalsPage = lazy(() => import('@/pages/ApprovalsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const ActivatePage = lazy(() => import('@/pages/ActivatePage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));

export function App() {
  const status = useAuthStore((s) => s.status);
  const restore = useAuthStore((s) => s.restore);
  const activeWorkspaceId = useUiStore((s) => s.activeWorkspaceId);
  const queryClient = useQueryClient();

  useEffect(() => {
    restore();
  }, [restore]);

  /**
   * Drops every cached query when the workspace changes.
   *
   * The scope of a cache entry is not in its key — the workspace travels as a
   * request header — so anything already cached belongs to the workspace it was
   * fetched in. Without this, switching paints the previous workspace's rows
   * until each query happens to refetch, which looks exactly like the isolation
   * failing.
   *
   * `resetQueries`, specifically:
   *
   * - `invalidateQueries` keeps serving the old data while it refetches, and
   *   here "stale" means another workspace's rows;
   * - `removeQueries` drops the cache but leaves mounted views holding what
   *   they already rendered, so nothing on screen changes until the page is
   *   reloaded by hand. That was the bug.
   *
   * `resetQueries` does both halves: clears the data *and* refetches whatever
   * is currently on screen, which by then carries the new workspace header.
   */
  const previousWorkspace = useRef(activeWorkspaceId);
  useEffect(() => {
    if (previousWorkspace.current === activeWorkspaceId) return;
    previousWorkspace.current = activeWorkspaceId;
    void queryClient.resetQueries();
  }, [activeWorkspaceId, queryClient]);

  if (status === 'idle' || status === 'loading') {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (status !== 'authenticated') {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<SsoCallbackPage />} />
          {/* Reachable without a session — these are how a session is obtained. */}
          <Route path="/activate" element={<ActivatePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      {/*
        Also reachable WITH a session, not only without one.
        This route existed solely in the unauthenticated table above, which is fine for a first
        sign-in and wrong for every one after it: somebody who already has a session here —
        arriving from the iKomet Hub launchpad, say — boots straight to `authenticated`, so
        /auth/callback fell through to the catch-all and rendered "Page not found" inside the
        app shell, with the tokens still sitting unread in the URL fragment and no redirect.

        Outside the AppLayout group deliberately: it is a hand-off that lasts a few hundred
        milliseconds, and framing it in the sidebar makes it look like a page that failed to
        load. SsoCallbackPage adopts whichever tokens it is given and forwards to `return_to`,
        so re-adopting a fresher session is correct rather than merely harmless.
      */}
      <Route path="/auth/callback" element={<SsoCallbackPage />} />
      <Route element={<AppLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/my-work" element={<MyWorkPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="/projects/:projectId/tasks/:taskId" element={<ProjectDetailPage />} />
        <Route path="/timesheet" element={<TimesheetPage />} />
        <Route path="/approvals" element={<ApprovalsPage />} />
        <Route path="/command-center" element={<CommandCenterPage />} />
        {/* One URL per report and per saved page, so a figure can be linked
            to in a message and land where it was sent from. */}
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/pages/:pageId" element={<ReportsPage />} />
        <Route path="/reports/:reportKey" element={<ReportsPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/people" element={<PeoplePage />} />
        {/* The review conversation gets its own URL so it can be opened and
            printed directly, without navigating through a report first. */}
        <Route path="/people/:userId/review" element={<PersonReviewPage />} />
        <Route path="/workspaces" element={<WorkspacesPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
